import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../utils/firebase';
import { Search, User, Check, X, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import debounce from 'lodash/debounce';

// Helper function to normalize text for searching by removing special characters
const normalizeForSearch = (text) => {
  if (!text) return '';
  return text.toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
    .trim();
};

const InspectorSearch = ({ onInspectorSelect, selectedInspectors = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [searchComplete, setSearchComplete] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [allInspectors, setAllInspectors] = useState([]); // Store all inspectors
  const [inspectorsLoaded, setInspectorsLoaded] = useState(false);
  const searchContainerRef = useRef(null);
  const searchAbortControllerRef = useRef(null);

  // Load all inspectors once when component mounts
  useEffect(() => {
    const loadAllInspectors = async () => {
      try {
        setIsLoading(true);
        
        // Get the current authenticated user
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
          console.error('No authenticated user found');
          setAllInspectors([]);
          setInspectorsLoaded(true);
          setIsLoading(false);
          return;
        }

        // Use hardcoded inspector list for simplicity and reliability
        const knownInspectors = [
          { id: 'kqOyS3ZgsuSQcSk4lJgAJnXjKHc2', name: 'Leo Betancor', email: 'leo@makeasmartchoice.us' },
          { id: 'VoqlpcFH8eTVC6N9JZpfYaKNUlY2', name: 'Brandon Vetere', email: 'brandon@makeasmartchoice.us' },
          { id: 'S1XmITa8lJZ36SR4Jf3UVsR1UMw1', name: 'Anthony Glavan', email: 'anthony@makeasmartchoice.us' }
        ];
        
        // Convert to standard format and mark current user
        const inspectors = knownInspectors.map(inspector => ({
          id: inspector.id,
          displayName: inspector.name,
          email: inspector.email,
          role: 'inspector',
          isCurrentUser: inspector.id === currentUser.uid
        }));
        
        console.log(`Using ${inspectors.length} hardcoded inspectors`);
        setAllInspectors(inspectors);
        setInspectorsLoaded(true);
      } catch (error) {
        console.error('Error in inspector loading:', error);
        setInspectorsLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadAllInspectors();
  }, []);

  const cancelOngoingSearch = () => {
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      searchAbortControllerRef.current = null;
    }
  };

  const resetSearchStates = (clearTerm = false) => {
    setIsLoading(false);
    setSearchComplete(false);
    setShowResults(false);
    
    if (clearTerm) {
      setSearchTerm('');
      setSearchResults([]);
      setIsSearchPerformed(false);
    }
  };

  const clearSearch = () => {
    resetSearchStates(true);
  };

  const handleSearch = async (term) => {
    if (!term || !term.trim()) {
      setSearchResults([]);
      setIsSearchPerformed(false);
      setSearchComplete(false);
      return;
    }

    // Cancel any ongoing search
    cancelOngoingSearch();

    // Create a new AbortController for this search
    searchAbortControllerRef.current = new AbortController();
    const signal = searchAbortControllerRef.current.signal;

    setIsLoading(true);
    setIsSearchPerformed(true);
    setSearchComplete(false);
    setShowResults(true);

    try {
      // Check if search was aborted
      if (signal.aborted) return;
      
      if (!inspectorsLoaded) {
        console.log('Inspectors not loaded yet, waiting...');
        // Wait for inspectors to load if they haven't yet
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (inspectorsLoaded) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      }
      
      // Search is now entirely client-side since we have all inspectors loaded
      const searchTermLower = term.toLowerCase().trim();
      const normalizedSearchTerm = normalizeForSearch(term);
      
      console.log('Searching for inspector:', searchTermLower);
      
      // Filter inspectors based on search term
      const results = allInspectors.filter(inspector => {
        // Check all relevant fields
        return (
          // Name/display name match
          (inspector.displayName && inspector.displayName.toLowerCase().includes(searchTermLower)) ||
          // Email match
          (inspector.email && inspector.email.toLowerCase().includes(searchTermLower))
        );
      }).map(inspector => {
        // Calculate relevance score for sorting
        let relevanceScore = 0;
        
        // Email match
        if (inspector.email && inspector.email.toLowerCase() === searchTermLower) {
          relevanceScore += 100; // Exact match
        } else if (inspector.email && inspector.email.toLowerCase().includes(searchTermLower)) {
          relevanceScore += 50; // Partial match
        }
        
        // Display name match
        if (inspector.displayName && inspector.displayName.toLowerCase() === searchTermLower) {
          relevanceScore += 90; // Exact match
        } else if (inspector.displayName && inspector.displayName.toLowerCase().includes(searchTermLower)) {
          relevanceScore += 45; // Partial match
        }
        
        // Current user gets a boost in relevance
        if (inspector.isCurrentUser) {
          relevanceScore += 10;
        }
        
        return {
          ...inspector,
          relevanceScore
        };
      }).sort((a, b) => {
        // Sort by relevance score
        const scoreCompare = b.relevanceScore - a.relevanceScore;
        if (scoreCompare !== 0) return scoreCompare;
        
        // If same score, sort alphabetically by name
        return a.displayName.localeCompare(b.displayName);
      });
      
      console.log(`Found ${results.length} matching inspectors`);
      
      // Update results
      setSearchResults(results);
    } catch (error) {
      // Only show error if not aborted
      if (!signal.aborted) {
        console.error('Error searching for inspectors:', error);
        toast.error('Error searching for inspectors');
        setSearchResults([]);
      }
    } finally {
      // Only update states if not aborted
      if (!signal.aborted) {
        setIsLoading(false);
        setSearchComplete(true);
      }
    }
  };

  // Create a debounced version of the search function
  const debouncedSearch = useCallback(
    debounce((term) => {
      handleSearch(term);
    }, 300),
    [allInspectors, inspectorsLoaded] // Re-create when inspectors change
  );

  // Trigger search when search term changes
  useEffect(() => {
    if (searchTerm.trim().length > 0) {
      setIsLoading(true);
      setSearchComplete(false);
      debouncedSearch(searchTerm);
    } else if (searchTerm.trim().length === 0) {
      cancelOngoingSearch();
      debouncedSearch.cancel();
      setSearchResults([]);
      setIsSearchPerformed(false);
      setSearchComplete(false);
      setShowResults(false);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Handle clicks outside of the search component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        resetSearchStates();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper to check if inspector is selected
  const isInspectorSelected = (inspectorId) => {
    return selectedInspectors.some(inspector => inspector.id === inspectorId);
  };

  // Handle clicking on a inspector to select
  const handleInspectorClick = (inspector) => {
    if (isInspectorSelected(inspector.id)) {
      // If already selected, remove it
      onInspectorSelect(inspector, true);
    } else {
      // Pass the selected inspector up to parent component
      onInspectorSelect(inspector, false);
    }
    
    // Reset the search states but keep the search term
    resetSearchStates();
  };

  // Handle removing an inspector from the selected list
  const handleRemoveInspector = (inspector, e) => {
    // Stop event propagation to prevent other click handlers
    e.stopPropagation();
    // Remove the inspector
    onInspectorSelect(inspector, true);
  };

  return (
    <div className="space-y-3" ref={searchContainerRef}>
      <div className="flex flex-col">
        <label htmlFor="inspector-search" className="text-sm font-medium text-slate-700 mb-1">
          Add Collaborating Inspectors
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-slate-400" />
          </div>
          <input
            type="text"
            name="inspector-search"
            id="inspector-search"
            className="block w-full pl-10 pr-10 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            onFocus={() => {
              if (searchTerm.trim().length > 0) {
                setShowResults(true);
              }
            }}
          />
          {searchTerm && (
            <div className="absolute inset-y-0 right-0 flex items-center">
              <button
                type="button"
                className="h-full px-2 text-slate-400 hover:text-slate-600"
                onClick={clearSearch}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Search Results */}
        {showResults && (
          <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg max-h-60 overflow-auto border border-slate-200">
            {isLoading ? (
              <div className="p-4 text-center text-sm text-slate-500">
                Searching...
              </div>
            ) : searchResults.length > 0 ? (
              <ul className="py-1">
                {searchResults.map((inspector) => (
                  <li
                    key={inspector.id}
                    className={`px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-slate-100
                      ${isInspectorSelected(inspector.id) ? 'bg-blue-50' : ''}`}
                    onClick={() => handleInspectorClick(inspector)}
                  >
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-slate-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-slate-700">
                          {inspector.displayName}
                          {inspector.isCurrentUser && <span className="ml-1 text-xs text-blue-500">(You)</span>}
                        </div>
                        <div className="text-xs text-slate-500">{inspector.email}</div>
                      </div>
                    </div>
                    {isInspectorSelected(inspector.id) ? (
                      <X className="h-4 w-4 text-red-500" />
                    ) : (
                      <Check className="h-4 w-4 text-green-500 opacity-0 group-hover:opacity-100" />
                    )}
                  </li>
                ))}
              </ul>
            ) : isSearchPerformed && searchComplete ? (
              <div className="p-4 text-center text-sm text-slate-500">
                No inspectors found
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Selected Inspectors */}
      {selectedInspectors.length > 0 && (
        <div className="mt-3">
          <div className="text-sm font-medium text-slate-700 mb-2">
            Collaborating Inspectors
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedInspectors.map((inspector) => (
              <div
                key={inspector.id}
                className="inline-flex items-center bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-sm"
              >
                <User className="h-3.5 w-3.5 mr-1.5" />
                <span>
                  {inspector.displayName}
                </span>
                <button
                  type="button"
                  className="ml-1.5 text-blue-400 hover:text-blue-600"
                  onClick={(e) => handleRemoveInspector(inspector, e)}
                  aria-label="Remove"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectorSearch; 